import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { HardDrive, Trash2, Loader2, RefreshCw, AlertTriangle, Lock, Clock, Link2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { usePager, PagerBar } from "@/components/admin/usePager";
import { R2StorageSection } from "@/components/admin/R2StorageSection";
import { useApp } from "@/context/AppContext";
import { liveTitle } from "@/lib/liveTitle";

const fmtDur = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${String(ss).padStart(2, "0")}`;
};
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const STALE_STATES = new Set(["pendingupload", "downloading", "queued"]);

const StatCard = ({ label, value, tone }) => (
  <div className="rounded-2xl border p-4" style={{ borderColor: "var(--yalola-border)", background: "#fff" }}>
    <div className="text-2xl font-extrabold" style={{ color: tone || "var(--yalola-text)" }}>{value}</div>
    <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
  </div>
);

export const StoragePanel = () => {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [confirm, setConfirm] = useState(null); // { mode, uids, count }
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeText, setPurgeText] = useState("");
  const [purging, setPurging] = useState(false);
  const [attach, setAttach] = useState(null); // { uid, name }
  const [attachData, setAttachData] = useState(null); // { lives, churches }
  const [attachMode, setAttachMode] = useState("live");
  const [attachLiveId, setAttachLiveId] = useState("");
  const [attachChurchId, setAttachChurchId] = useState("");
  const [attachTitle, setAttachTitle] = useState("");
  const [attaching, setAttaching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/stream/storage");
      setData(res.data);
      setSel(new Set());
    } catch {
      toast.error("Impossible de charger le stockage vidéo");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const videos = data?.videos || [];
  const summary = data?.summary || {};
  const selectable = useMemo(() => videos.filter((v) => !v.referenced), [videos]);
  const pager = usePager(videos);

  const toggle = (uid) => setSel((s) => {
    const n = new Set(s);
    n.has(uid) ? n.delete(uid) : n.add(uid);
    return n;
  });
  const selNames = useMemo(() => videos.filter((v) => sel.has(v.uid)).map((v) => v.name || v.uid), [videos, sel]);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data: r } = await api.post("/admin/stream/reconcile-replays", {});
      toast.success(`${r.linked} rediffusion(s) reliée(s)${r.published ? ` · ${r.published} publiée(s)` : ""}${r.deduped ? ` · ${r.deduped} doublon(s) supprimé(s)` : ""}`);
      await load();
    } catch {
      toast.error("Échec de la réconciliation des rediffusions");
    } finally {
      setReconciling(false);
    }
  };

  const runCleanup = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const body = confirm.mode === "selected" ? { mode: "selected", uids: [...confirm.uids] } : { mode: confirm.mode };
      const { data: r } = await api.post("/admin/stream/cleanup", body);
      toast.success(`${r.deleted} vidéo(s) supprimée(s)${r.skipped_referenced ? ` · ${r.skipped_referenced} protégée(s)` : ""}`);
      setConfirm(null);
      await load();
    } catch {
      toast.error("Échec de la suppression");
    } finally {
      setBusy(false);
    }
  };

  const openAttach = async (v) => {
    setAttach({ uid: v.uid, name: v.name || v.uid });
    setAttachMode("live"); setAttachLiveId(""); setAttachChurchId("");
    setAttachTitle(v.name || "Rediffusion");
    setAttachData(null);
    try { const { data: c } = await api.get("/admin/stream/attach-candidates"); setAttachData(c); }
    catch { toast.error("Impossible de charger les cibles de rattachement"); }
  };
  const runAttach = async () => {
    if (!attach) return;
    setAttaching(true);
    try {
      const body = attachMode === "live"
        ? { uid: attach.uid, mode: "live", live_id: attachLiveId }
        : { uid: attach.uid, mode: "church", church_id: attachChurchId, title: attachTitle };
      await api.post("/admin/stream/attach", body);
      toast.success("Vidéo rattachée — rediffusion publiée ✅");
      setAttach(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du rattachement");
    } finally {
      setAttaching(false);
    }
  };

  const openPurge = async () => {
    setPurgeText(""); setPurgePreview(null); setPurgeOpen(true);
    try { const { data: p } = await api.get("/admin/videos/purge/preview"); setPurgePreview(p); }
    catch { /* preview is optional */ }
  };
  const runPurge = async () => {
    setPurging(true);
    try {
      const { data: r } = await api.post("/admin/videos/purge", { confirm: "REPARTIR PROPRE" });
      toast.success(`Nettoyage terminé — ${r.cloudflare_deleted || 0} Cloudflare + ${r.bunny_deleted || 0} Bunny, ${r.posts_deleted || 0} post(s), ${r.lives_deleted || 0} direct(s)`);
      setPurgeOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du nettoyage");
    } finally {
      setPurging(false);
    }
  };

  if (data && data.configured === false) {
    return (
      <div className="rounded-2xl border p-6 text-center" style={{ borderColor: "var(--yalola-border)" }} data-testid="admin-storage-not-configured">
        <HardDrive className="mx-auto mb-3" size={28} style={{ color: "var(--yalola-text-muted)" }} />
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Cloudflare Stream n'est pas configuré. Renseigne les clés dans « Intégrations / API ».</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-storage-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><HardDrive size={20} style={{ color: "#D97757" }} /> Stockage vidéo</h2>
          <p className="text-sm mt-1" style={{ color: "var(--yalola-text-muted)" }}>Gère les vidéos stockées sur Cloudflare Stream. Supprime les vidéos inutiles pour libérer des minutes.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading || busy || reconciling} data-testid="storage-refresh-btn">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Actualiser
        </Button>
      </div>

      <div className="rounded-2xl border p-3 flex items-start gap-3 flex-wrap" style={{ borderColor: "var(--yalola-border)", background: "#F0FDF4" }}>
        <Link2 size={16} className="shrink-0 mt-0.5" style={{ color: "#047857" }} />
        <div className="flex-1 min-w-0 text-xs" style={{ color: "#065F46" }}>
          <strong>Rediffusions non liées ?</strong> Certains de tes vrais directs peuvent apparaître « Non liée » car l'app n'avait pas rattaché leur enregistrement Cloudflare. Lance la réconciliation : chaque direct sera relié à son enregistrement (le plus long) et sa rediffusion publiée. Les rediffusions en <strong>double</strong> (un même direct affiché plusieurs fois dans le fil) sont fusionnées en une seule — aucune vidéo Cloudflare n'est supprimée.
        </div>
        <Button variant="outline" onClick={reconcile} disabled={reconciling || busy || loading} data-testid="storage-reconcile-btn">
          {reconciling ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Réconcilier les rediffusions
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Minutes stockées" value={summary.minutes ?? "—"} />
        <StatCard label="Vidéos" value={summary.count ?? "—"} />
        <StatCard label="Non liées" value={summary.orphans ?? "—"} tone="#B45309" />
        <StatCard label="Uploads bloqués" value={summary.stale ?? "—"} tone="#DC2626" />
      </div>

      <div className="rounded-2xl border p-3 flex items-start gap-2 text-xs" style={{ borderColor: "#FCD34D", background: "#FFFBEB", color: "#92400E" }}>
        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
        <span>La suppression est <strong>définitive</strong>. Les vidéos « non liées » peuvent inclure d'anciens directs que tu veux garder — vérifie chaque nom avant de supprimer. Les vidéos <strong>utilisées</strong> et les preuves de <strong>quarantaine</strong> sont protégées et ne peuvent pas être supprimées ici.</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          disabled={busy || loading || !summary.stale}
          onClick={() => setConfirm({ mode: "stale", count: summary.stale })}
          data-testid="storage-clean-stale-btn"
        >
          <Clock size={15} /> Supprimer les uploads bloqués ({summary.stale || 0})
        </Button>
        <Button
          disabled={busy || loading || sel.size === 0}
          onClick={() => setConfirm({ mode: "selected", uids: sel, count: sel.size })}
          style={{ background: sel.size ? "#DC2626" : undefined, color: sel.size ? "#fff" : undefined }}
          data-testid="storage-delete-selected-btn"
        >
          <Trash2 size={15} /> Supprimer la sélection ({sel.size})
        </Button>
        {selectable.length > 0 && (
          <button
            className="text-xs underline"
            style={{ color: "var(--yalola-text-muted)" }}
            onClick={() => setSel((s) => (s.size === selectable.length ? new Set() : new Set(selectable.map((v) => v.uid))))}
            data-testid="storage-select-all-btn"
          >
            {sel.size === selectable.length ? "Tout désélectionner" : "Tout sélectionner (non liées)"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="animate-spin mx-auto" style={{ color: "#D97757" }} /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="storage-video-grid">
          {pager.slice.map((v) => {
            const stale = v.stale;
            const ref = v.referenced;
            const checked = sel.has(v.uid);
            return (
              <div
                key={v.uid}
                className="rounded-2xl border overflow-hidden flex flex-col"
                style={{ borderColor: checked ? "#DC2626" : "var(--yalola-border)", background: "#fff" }}
                data-testid={`storage-video-${v.uid}`}
              >
                <div className="relative aspect-video bg-neutral-100">
                  {v.thumbnail ? (
                    <img src={v.thumbnail} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full grid place-items-center"><HardDrive size={22} style={{ color: "#cbd5e1" }} /></div>
                  )}
                  <span
                    className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold"
                    style={ref
                      ? { background: "rgba(16,185,129,0.15)", color: "#047857" }
                      : stale
                      ? { background: "rgba(220,38,38,0.15)", color: "#B91C1C" }
                      : { background: "rgba(180,83,9,0.15)", color: "#B45309" }}
                  >
                    {ref ? "Utilisée" : stale ? "Upload bloqué" : "Non liée"}
                  </span>
                  {!ref && (
                    <div className="absolute top-2 right-2">
                      <Checkbox checked={checked} onCheckedChange={() => toggle(v.uid)} data-testid={`storage-check-${v.uid}`} className="bg-white/90 border-neutral-400" />
                    </div>
                  )}
                  {ref && <Lock size={13} className="absolute top-2.5 right-2.5" style={{ color: "#047857" }} />}
                </div>
                <div className="p-3 flex-1 flex flex-col gap-1">
                  <div className="text-sm font-semibold truncate" title={v.name}>{v.name || v.uid}</div>
                  <div className="text-xs flex items-center gap-2" style={{ color: "var(--yalola-text-muted)" }}>
                    <span>{fmtDur(v.duration)}</span>
                    <span>·</span>
                    <span>{fmtDate(v.created)}</span>
                  </div>
                  {v.status && v.status !== "ready" && (
                    <div className="text-[11px] font-medium" style={{ color: "#B91C1C" }}>{v.status}</div>
                  )}
                  {!ref && !stale && v.status === "ready" && (
                    <Button size="sm" variant="outline" className="mt-1 w-fit" disabled={busy || attaching}
                      onClick={() => openAttach(v)} data-testid={`stream-attach-${v.uid}`}>
                      <Link2 size={13} className="mr-1" /> Rattacher
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!loading && <PagerBar page={pager.page} pages={pager.pages} total={pager.total} shown={pager.slice.length} onPage={pager.setPage} noun="vidéo" testId="storage-pager" />}

      {/* Feed/Reels files on R2 + abandoned multipart uploads */}
      <R2StorageSection />

      <div className="rounded-2xl border p-4 space-y-3" style={{ borderColor: "#FCA5A5", background: "#FEF2F2" }} data-testid="storage-purge-section">
        <div className="flex items-center gap-2">
          <ShieldAlert size={18} style={{ color: "#DC2626" }} />
          <h3 className="text-base font-bold" style={{ color: "#991B1B" }}>Repartir propre — vidéos</h3>
        </div>
        <p className="text-xs" style={{ color: "#991B1B" }}>
          Supprime <strong>TOUTES</strong> les vidéos (rediffusions, reels, vidéos du fil + directs) sur <strong>Cloudflare Stream</strong> et <strong>Bunny</strong>, ainsi que les posts et directs correspondants — pour repartir sur une base propre. <strong>Jamais</strong> tes photos, messages, réactions ni tes fichiers R2. Action <strong>définitive</strong>.
        </p>
        <Button
          onClick={openPurge}
          disabled={busy || loading || purging}
          style={{ background: "#DC2626", color: "#fff" }}
          data-testid="storage-purge-open-btn"
        >
          <Trash2 size={15} /> Repartir propre (tout effacer les vidéos)
        </Button>
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o && !busy) setConfirm(null); }}>
        <DialogContent data-testid="storage-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer {confirm?.count} vidéo{confirm?.count > 1 ? "s" : ""} ?</DialogTitle>
            <DialogDescription>
              {confirm?.mode === "stale"
                ? "Supprime uniquement les uploads jamais terminés (bloqués depuis plus de 24 h)."
                : "Cette action est définitive. Les vidéos encore utilisées ou en quarantaine seront automatiquement ignorées."}
            </DialogDescription>
          </DialogHeader>
          {confirm?.mode === "selected" && (
            <div className="max-h-40 overflow-auto rounded-xl border p-2 text-xs space-y-1" style={{ borderColor: "var(--yalola-border)" }}>
              {selNames.map((n, i) => <div key={i} className="truncate">• {n}</div>)}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy} data-testid="storage-confirm-cancel">Annuler</Button>
            <Button onClick={runCleanup} disabled={busy} style={{ background: "#DC2626", color: "#fff" }} data-testid="storage-confirm-delete">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!attach} onOpenChange={(o) => { if (!o && !attaching) setAttach(null); }}>
        <DialogContent data-testid="stream-attach-dialog">
          <DialogHeader>
            <DialogTitle>Rattacher la vidéo</DialogTitle>
            <DialogDescription className="truncate">« {attach?.name} » sera reliée et sa rediffusion publiée.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <button type="button" onClick={() => setAttachMode("live")} data-testid="stream-attach-mode-live"
              className="flex-1 rounded-xl border px-3 py-2 text-sm font-medium"
              style={attachMode === "live" ? { borderColor: "#D97757", background: "#FFF7ED", color: "#9A3412" } : { borderColor: "var(--yalola-border)" }}>
              À un direct existant
            </button>
            <button type="button" onClick={() => setAttachMode("church")} data-testid="stream-attach-mode-church"
              className="flex-1 rounded-xl border px-3 py-2 text-sm font-medium"
              style={attachMode === "church" ? { borderColor: "#D97757", background: "#FFF7ED", color: "#9A3412" } : { borderColor: "var(--yalola-border)" }}>
              À une église
            </button>
          </div>
          {!attachData ? (
            <div className="flex items-center gap-2 text-xs py-2" style={{ color: "var(--yalola-text-muted)" }}>
              <Loader2 size={14} className="animate-spin" /> Chargement des cibles…
            </div>
          ) : attachMode === "live" ? (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold">Direct sans rediffusion</label>
              <select value={attachLiveId} onChange={(e) => setAttachLiveId(e.target.value)}
                className="w-full rounded-xl border px-3 py-2 text-sm bg-white"
                style={{ borderColor: "var(--yalola-border)" }} data-testid="stream-attach-live-select">
                <option value="">— Choisir un direct —</option>
                {(attachData.lives || []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {liveTitle(l, t, { name: l.host_name })} · {fmtDate(l.started_at || l.created_at)} · {l.host_name || "?"}{l.status === "waiting" ? " · en préparation" : ""}
                  </option>
                ))}
              </select>
              {(attachData.lives || []).length === 0 && (
                <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Aucun direct sans rediffusion — utilise plutôt « À une église ».</p>
              )}
              <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>La vidéo devient la rediffusion de ce direct (terminé automatiquement si besoin).</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold">Église</label>
                <select value={attachChurchId} onChange={(e) => setAttachChurchId(e.target.value)}
                  className="w-full rounded-xl border px-3 py-2 text-sm bg-white"
                  style={{ borderColor: "var(--yalola-border)" }} data-testid="stream-attach-church-select">
                  <option value="">— Choisir une église —</option>
                  {(attachData.churches || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold">Titre de la rediffusion</label>
                <Input value={attachTitle} onChange={(e) => setAttachTitle(e.target.value)} placeholder="Titre" data-testid="stream-attach-title-input" />
              </div>
              <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Crée une rediffusion sur la page de l'église (section « Directs & rediffusions ») et la publie dans le fil au nom du responsable.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttach(null)} disabled={attaching} data-testid="stream-attach-cancel">Annuler</Button>
            <Button onClick={runAttach} data-testid="stream-attach-confirm"
              disabled={attaching || !attachData || (attachMode === "live" ? !attachLiveId : !attachChurchId)}>
              {attaching ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Rattacher
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={purgeOpen} onOpenChange={(o) => { if (!o && !purging) setPurgeOpen(false); }}>
        <DialogContent data-testid="storage-purge-dialog">
          <DialogHeader>
            <DialogTitle>Repartir propre — supprimer toutes les vidéos ?</DialogTitle>
            <DialogDescription>
              Cette action est <strong>définitive</strong>. Elle ne touche ni tes photos, ni tes messages, ni tes fichiers R2.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border p-3 text-sm space-y-1" style={{ borderColor: "var(--yalola-border)" }} data-testid="storage-purge-preview">
            {purgePreview ? (
              <>
                <div className="flex justify-between"><span>Vidéos Cloudflare Stream</span><strong>{purgePreview.cloudflare_videos}</strong></div>
                <div className="flex justify-between"><span>Vidéos Bunny</span><strong>{purgePreview.bunny_videos}</strong></div>
                <div className="flex justify-between"><span>Posts vidéo / reels / rediffusions</span><strong>{purgePreview.video_posts}</strong></div>
                <div className="flex justify-between"><span>Directs</span><strong>{purgePreview.lives}</strong></div>
                <div className="flex justify-between"><span>Directs programmés</span><strong>{purgePreview.scheduled_lives}</strong></div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-xs" style={{ color: "var(--yalola-text-muted)" }}><Loader2 size={14} className="animate-spin" /> Calcul de l'impact…</div>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold" style={{ color: "#991B1B" }}>Tape exactement <code>REPARTIR PROPRE</code> pour confirmer</label>
            <Input value={purgeText} onChange={(e) => setPurgeText(e.target.value)} placeholder="REPARTIR PROPRE" data-testid="storage-purge-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeOpen(false)} disabled={purging} data-testid="storage-purge-cancel">Annuler</Button>
            <Button onClick={runPurge} disabled={purging || purgeText.trim() !== "REPARTIR PROPRE"} style={{ background: "#DC2626", color: "#fff" }} data-testid="storage-purge-run">
              {purging ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Tout supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoragePanel;
